import React, { useCallback, useMemo } from 'react';

import moment from 'moment';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

import { BsCheck, BsFillLightningFill } from 'react-icons/bs';
import { HiOutlineDocumentText } from 'react-icons/hi';

import Search from '~/components/Search';
import DefaultLoader from '~/components/DefaultLoader';
import FormDefault from '~/components/FormDefault';

import { Container } from './styles';
import { useAlteracaoPrecoEntradaFornecedor } from './AlteracaoPrecoEntradaFornecedorContext';

import TableContainerComponent from './Component/TableContainerComponent';
import Cards from './Component/Cards';
import { ModalPDF } from './Component/ModalDanfe/ModalPDF';
import { CustomButtonNovo } from '~/components/Buttons/CustomButtonNovo';

const COD_TELA = 210;

const AlteracaoPrecoEntradaFornecedorContent: React.FC = () => {
  const {
    showSearch,
    onRowClick,
    loader,
    codFornecedor,
    isUpdate,
    resetFormData,
    setShowSearch,
    validateNFConferida,
    descartarAlteracoes,
    marcarNFConferida,
    conferida,
    aplicarAlteracoes,
    loja,
    cnpj,
    numNf,
    entrada,
    fornecedor,
    codSeqNF,
    isOpenModal,
    setLoadingPDF,
    getDanfe,
    loadingPDF,
  } = useAlteracaoPrecoEntradaFornecedor();

  const mySwal = useMemo(() => withReactContent(Swal), []);

  const handleConfirmarPendencias = useCallback(async (): Promise<boolean> => {
    const result = await mySwal.fire({
      title: '',
      text: 'Alterações de preços pendentes? Deseja',
      width: 800,
      showCancelButton: true,
      showDenyButton: true,
      confirmButtonColor: '#0065FF',
      denyButtonColor: '#ffc107',
      cancelButtonColor: '#DE350B',
      confirmButtonText: 'Aplicar Alterações',
      denyButtonText: 'Descartar e apenas Confirmar',
      cancelButtonText: 'Cancelar',
    });

    if (result.isConfirmed) {
      aplicarAlteracoes();
      return true;
    }

    if (result.isDenied) {
      descartarAlteracoes();
      marcarNFConferida();
    }

    return false;
  }, [aplicarAlteracoes, descartarAlteracoes, marcarNFConferida, mySwal]);

  const handleDanfeClick = useCallback(() => {
    setLoadingPDF(true);
    getDanfe();
  }, [getDanfe, setLoadingPDF]);

  const handleConferidaClick = useCallback(async () => {
    const temPendencias = validateNFConferida();

    if (temPendencias) {
      await handleConfirmarPendencias();
      return;
    }

    marcarNFConferida();
  }, [handleConfirmarPendencias, marcarNFConferida, validateNFConferida]);

  const headerInfo = useMemo(
    () => (
      <div className="col-sm-12 table-exibition">
        <table className="table">
          <thead>
            <tr>
              <th>Loja</th>
              <th>Fornecedor</th>
              <th>CNPJ</th>
              <th>NF</th>
              <th>Entrada</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{loja}</td>
              <td>{`${codFornecedor} - ${fornecedor}`}</td>
              <td>{cnpj}</td>
              <td>{numNf}</td>
              <td>{moment(entrada).format('DD/MM/YYYY')}</td>
            </tr>
          </tbody>
        </table>
      </div>
    ),
    [cnpj, codFornecedor, entrada, fornecedor, loja, numNf],
  );

  const customButtonsUpdate = useMemo(
    () => (
      <div className="row">
        <div
          className="col-12"
          style={{ display: 'flex', justifyContent: 'end', gap: '10px' }}
        >
          <CustomButtonNovo
            style={{ backgroundColor: '#2d136c' }}
            disabled={loadingPDF}
            onClick={handleDanfeClick}
            ariaLabel="Abrir DANFE"
            aria-busy={loadingPDF}
            showLoading={loadingPDF}
            showLoadingIcon={loadingPDF}
            icon={HiOutlineDocumentText}
            iconSize={28}
            width="auto"
            label="DANFE"
          />

          <CustomButtonNovo
            label={conferida ? 'NF JÁ CONFERIDA!' : 'MARCAR NF CONFERIDA?'}
            disabled={conferida}
            style={{
              backgroundColor: conferida ? 'green' : '#007bff',
            }}
            onClick={handleConferidaClick}
            ariaLabel={
              conferida
                ? 'Nota fiscal já conferida'
                : 'Marcar nota fiscal como conferida'
            }
            icon={BsCheck}
            iconSize={40}
            width="auto"
          />

          <CustomButtonNovo
            label="APLICAR ALTERAÇÕES"
            onClick={aplicarAlteracoes}
            icon={BsFillLightningFill}
            iconSize={20}
            width="auto"
          />
        </div>
      </div>
    ),
    [
      aplicarAlteracoes,
      conferida,
      handleConferidaClick,
      handleDanfeClick,
      loadingPDF,
    ],
  );

  if (loader) {
    return (
      <Container>
        <DefaultLoader />
      </Container>
    );
  }

  return (
    <Container>
      {showSearch ? (
        <Search
          codTela={COD_TELA}
          newData={() => []}
          onRowClick={onRowClick}
          onlyReport
        />
      ) : (
        <FormDefault
          codTela={COD_TELA}
          title="Alteração de Preços por Entrada de Fornecedor"
          codigoRegistro={[{ value: codSeqNF, des_campo: 'Cód. Seq. NF' }]}
          onSave={() => Promise.resolve()}
          onCancel={() => setShowSearch(true)}
          isUpdate={isUpdate}
          onNew={resetFormData}
          onDelete={() => []}
          onClearFields={resetFormData}
          onReturnSearch={() => {
            resetFormData();
            setShowSearch(true);
          }}
          isClear={false}
          isSave
          isNew={false}
          isDelete={false}
          customButtonsUpdate={customButtonsUpdate}
        >
          {headerInfo}

          <TableContainerComponent />
          <Cards />

          {isOpenModal && <ModalPDF />}
        </FormDefault>
      )}
    </Container>
  );
};

export default AlteracaoPrecoEntradaFornecedorContent;
